import { useState, type FormEvent } from "react";
import { Alan, Buton, MetinAlani } from "@koclukpanel/ui";
import type { SiparisAdresi } from "./magaza-tipleri";

/**
 * TESLİMAT ADRESİ FORMU — F-GAM-17, sürüm 7a. Öğrenci (18 ve üstü) ve veli (18 altı onayı) AYNI formu kullanır:
 * iki yerde iki doğrulama olsaydı biri geride kalırdı.
 *
 * Adres kişisel veridir: form yalnız toplar; saklama, KVKK silme (teslim/iptalden 6 ay sonra) ve kimin okuyacağı
 * SUNUCUDA. Doğrulama burada yalnız "boş bırakma / telefon biçimi" nezaketi; son söz sunucunun.
 * Dokunma hedefi ve etiketler `Alan` bileşeninden (mobil, tek el).
 */

export interface AdresFormuProps {
  /** Düğme yazısı: "Siparişi onayla" gibi eylemi söyler, "Gönder" demez. */
  gonderEtiketi: string;
  onGonder: (adres: SiparisAdresi) => void;
  yukleniyor?: boolean;
  /** Sunucudan gelen hata cümlesi. */
  hata?: string | null;
  onVazgec?: () => void;
  baslangic?: Partial<SiparisAdresi>;
}

const TELEFON_RAKAM = /^[0-9]{10,11}$/;

export function AdresFormu({
  gonderEtiketi,
  onGonder,
  yukleniyor = false,
  hata,
  onVazgec,
  baslangic,
}: AdresFormuProps) {
  const [aliciAdi, setAliciAdi] = useState(baslangic?.aliciAdi ?? "");
  const [telefon, setTelefon] = useState(baslangic?.telefon ?? "");
  const [il, setIl] = useState(baslangic?.il ?? "");
  const [ilce, setIlce] = useState(baslangic?.ilce ?? "");
  const [adres, setAdres] = useState(baslangic?.adres ?? "");
  const [postaKodu, setPostaKodu] = useState(baslangic?.postaKodu ?? "");
  const [denendi, setDenendi] = useState(false);

  const telefonRakami = telefon.replace(/[\s()-]/g, "");
  const eksik = (v: string) =>
    denendi && v.trim() === "" ? "Bu alan boş bırakılamaz." : undefined;
  const telefonHatasi =
    denendi && telefon.trim() !== "" && !TELEFON_RAKAM.test(telefonRakami)
      ? "Telefonu 10 ya da 11 rakam olarak yazın."
      : eksik(telefon);

  function gonder(e: FormEvent) {
    e.preventDefault();
    setDenendi(true);
    const bos = [aliciAdi, telefon, il, ilce, adres].some((v) => v.trim() === "");
    if (bos || !TELEFON_RAKAM.test(telefonRakami)) return;
    onGonder({
      aliciAdi: aliciAdi.trim(),
      telefon: telefonRakami,
      il: il.trim(),
      ilce: ilce.trim(),
      adres: adres.trim(),
      ...(postaKodu.trim() ? { postaKodu: postaKodu.trim() } : {}),
    });
  }

  return (
    <form onSubmit={gonder} noValidate className="flex flex-col gap-3">
      <Alan
        etiket="Alıcı adı soyadı"
        zorunlu
        autoComplete="name"
        value={aliciAdi}
        onChange={(e) => setAliciAdi(e.target.value)}
        hata={eksik(aliciAdi)}
      />
      <Alan
        etiket="Telefon"
        zorunlu
        type="tel"
        inputMode="tel"
        autoComplete="tel"
        value={telefon}
        onChange={(e) => setTelefon(e.target.value)}
        hata={telefonHatasi}
        yardim="Kargo firması teslimat için arar."
      />
      <div className="grid grid-cols-2 gap-3">
        <Alan
          etiket="İl"
          zorunlu
          autoComplete="address-level1"
          value={il}
          onChange={(e) => setIl(e.target.value)}
          hata={eksik(il)}
        />
        <Alan
          etiket="İlçe"
          zorunlu
          autoComplete="address-level2"
          value={ilce}
          onChange={(e) => setIlce(e.target.value)}
          hata={eksik(ilce)}
        />
      </div>
      <MetinAlani
        etiket="Açık adres"
        zorunlu
        rows={3}
        autoComplete="street-address"
        value={adres}
        onChange={(e) => setAdres(e.target.value)}
        hata={eksik(adres)}
      />
      <Alan
        etiket="Posta kodu (isteğe bağlı)"
        inputMode="numeric"
        autoComplete="postal-code"
        value={postaKodu}
        onChange={(e) => setPostaKodu(e.target.value)}
      />
      {hata && (
        <p role="alert" className="rounded-alan bg-hata-50 text-hata-700 px-3 py-2 text-sm">
          {hata}
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        <Buton type="submit" yukleniyor={yukleniyor}>
          {gonderEtiketi}
        </Buton>
        {onVazgec && (
          <Buton type="button" gorunum="sessiz" onClick={onVazgec} disabled={yukleniyor}>
            Vazgeç
          </Buton>
        )}
      </div>
    </form>
  );
}
